import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useFormat } from "hooks/useFormat";
import Table, { ColumnProps } from "components/basic/table";
import AutoComplete from "components/basic/autoComplete";
import Button from "components/basic/button";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import UnarchiveOutlinedIcon from "@mui/icons-material/UnarchiveOutlined";
import Typography from "components/basic/typography";
import VisibilityIcon from "@mui/icons-material/Visibility";
import { Chip, IconButton, Tooltip, useTheme } from "@mui/material";
import MessageIcon from "@mui/icons-material/Message";
import Badge from "components/basic/badge";
import { Lead } from "..";
import { formatExpiry } from "../../quotes/expiryHelpers";
import { quoteStatusMeta } from "../../quotes/quoteStatus";
import {
  STAGE_OPTIONS,
  getStageIdFromLabel,
  ASSIGNEE_OPTIONS,
  getAssigneeIdFromLabel,
} from "../constants";

/**
 * Stage and assignment are not part of the workflow yet, so the two columns are
 * hidden. Everything behind them — the options, the handlers, the `updateQuote`
 * call — is left wired up, so bringing them back is this flag alone.
 */
const SHOW_PIPELINE_COLUMNS: boolean = false;

export interface LeadTableProps {
  leads: Lead[];
  onStageChange?: (leadId: string | number, newStageId: number) => void;
  onAssigneeChange?: (leadId: string | number, newAssigneeId: number) => void;
  onView?: (leadId: number) => void;
  onMessage?: (customerId: number) => void;
  onRenew?: (leadId: number) => void;
  onToggleStar?: (lead: Lead) => void;
  onToggleArchive?: (lead: Lead) => void;
  /** Shown instead of the table when there are no leads. */
  emptyText?: string;
}

const LeadTable = ({
  leads,
  onStageChange,
  onAssigneeChange,
  onView,
  onMessage,
  onRenew,
  onToggleStar,
  onToggleArchive,
  emptyText,
}: LeadTableProps) => {
  const theme = useTheme();
  const { t } = useTranslation("leads");
  const { t: tQuotes } = useTranslation("quotes");
  const format = useFormat();

  const columns: ColumnProps<Lead>[] = useMemo(
    () => [
      {
        header: t("table.name"),
        align: "left",
        cell: (row: Lead) => (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: theme.spacing(1),
            }}
          >
            <IconButton
              size="small"
              aria-label={t(row.starred ? "actions.unstar" : "actions.star")}
              onClick={() => onToggleStar?.(row)}
            >
              {row.starred ? <StarIcon color="warning" /> : <StarBorderIcon />}
            </IconButton>
            {/* Bold until opened, like an unread message. */}
            <div
              style={{
                cursor: "pointer",
                fontWeight: row.viewed ? undefined : "bold",
              }}
              onClick={() => onView?.(row.id)}
            >
              {row.name}
            </div>
          </div>
        ),
      },
      {
        header: t("table.address"),
        align: "left",
        cell: (row: Lead) => row.address,
      },
      {
        header: t("table.distance"),
        align: "left",
        width: "80px",
        cell: (row: Lead) => t("table.distance_km", { distance: row.distance }),
      },
      {
        header: t("table.date_created"),
        align: "left",
        width: "80px",
        cell: (row: Lead) => format.date(Number(row.createdAt)),
      },
      {
        header: t("table.expires"),
        align: "left",
        width: "160px",
        cell: (row: Lead) => {
          const expiry = formatExpiry(
            tQuotes,
            format.date,
            row.expiresAt,
            row.isExpired,
          );

          return (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: theme.spacing(1),
              }}
              title={expiry.date}
            >
              <Chip
                size="small"
                label={
                  expiry.isExpired ? tQuotes("expiry.expired") : expiry.label
                }
                color={expiry.isExpired ? "default" : "success"}
                variant={expiry.isExpired ? "filled" : "outlined"}
              />
              {expiry.isExpired && onRenew && (
                <Button size="small" onClick={() => onRenew(row.id)}>
                  {t("actions.renew")}
                </Button>
              )}
            </div>
          );
        },
      },
      {
        header: t("table.approval"),
        align: "left",
        width: "140px",
        cell: (row: Lead) => {
          const meta = quoteStatusMeta(row.approvalStatus, "vendor");

          return <Badge label={tQuotes(meta.label)} color={meta.color} />;
        },
      },
      ...(SHOW_PIPELINE_COLUMNS
        ? [
            {
              header: t("table.stage"),
              align: "left" as const,
              width: "180px",
              cell: (row: Lead) => (
                <div style={{ minWidth: "180px" }}>
                  <AutoComplete
                    options={STAGE_OPTIONS}
                    value={getStageIdFromLabel(row.stage)}
                    onChange={(selectedOption) => {
                      if (selectedOption && onStageChange) {
                        onStageChange(row.id, selectedOption.id);
                      }
                    }}
                    size="tiny"
                    disabled={!onStageChange}
                    noBorder={true}
                  />
                </div>
              ),
            },
            {
              header: t("table.assigned_to"),
              align: "left" as const,
              width: "150px",
              cell: (row: Lead) => (
                <div style={{ minWidth: "150px" }}>
                  <AutoComplete
                    options={ASSIGNEE_OPTIONS}
                    value={getAssigneeIdFromLabel(row.assignedTo)}
                    onChange={(selectedOption) => {
                      if (selectedOption && onAssigneeChange) {
                        onAssigneeChange(row.id, selectedOption.id);
                      }
                    }}
                    size="tiny"
                    disabled={!onAssigneeChange}
                    noBorder={true}
                  />
                </div>
              ),
            },
          ]
        : []),
      {
        header: "",
        align: "right",
        width: "130px",
        card: "actions",
        cell: (row: Lead) => (
          <div
            style={{
              display: "flex",
              justifyContent: "flex-end",
              gap: theme.spacing(1),
            }}
          >
            <VisibilityIcon
              color="primary"
              style={{ cursor: "pointer" }}
              onClick={() => onView?.(row.id)}
            />
            <MessageIcon
              color="warning"
              style={{ cursor: "pointer" }}
              onClick={() => onMessage?.(row.customerId)}
            />
            {onToggleArchive && (
              <Tooltip
                title={t(
                  row.archived ? "actions.unarchive" : "actions.archive",
                )}
              >
                {row.archived ? (
                  <UnarchiveOutlinedIcon
                    color="action"
                    style={{ cursor: "pointer" }}
                    onClick={() => onToggleArchive(row)}
                  />
                ) : (
                  <ArchiveOutlinedIcon
                    color="action"
                    style={{ cursor: "pointer" }}
                    onClick={() => onToggleArchive(row)}
                  />
                )}
              </Tooltip>
            )}
          </div>
        ),
      },
    ],
    [
      theme,
      t,
      tQuotes,
      format,
      onStageChange,
      onAssigneeChange,
      onView,
      onMessage,
      onRenew,
      onToggleStar,
      onToggleArchive,
    ],
  );

  if (!leads.length) {
    return (
      <Typography
        variant="body1"
        align="center"
        style={{ padding: theme.spacing(4), opacity: 0.7 }}
      >
        {emptyText ?? t("empty.all")}
      </Typography>
    );
  }

  return <Table columns={columns} data={leads} />;
};

export default LeadTable;
